import { useQuery } from "@apollo/client";
import {
	byAsc,
	byDesc,
	defaultDateFormat,
	distinct,
	distinctBy,
	isEmptyValue,
	minBy,
	parseDate,
	sortByComparisons,
	sortBySelectors,
} from "@lyfegen-platform/utils";
import {
	addMonths,
	addYears,
	endOfDay,
	endOfMonth,
	endOfYear,
	format,
	parse,
	startOfDay,
	startOfMonth,
	startOfQuarter,
	startOfWeek,
	startOfYear,
} from "date-fns";
import hashSum from "hash-sum";
import { identity } from "lodash";
import React, { useEffect, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { GET_AGREEMENTS_FILTERS, GET_PUBLIC_AGREEMENTS_FILTERS } from "../../containers/agreements/queries";
import {
	GetAgreementsFilters,
	GetAgreementsFilters_GetAgreementsTableSourceFilterData,
} from "../../containers/agreements/types/GetAgreementsFilters";
import { GetAgreementsQuery, GetPublicAgreementsFiltersQuery } from "../../containers/agreements/types/queries.generated";
import { GET_CASES_FILTERS } from "../../containers/cases/queries";
import { GetCasesFiltersQuery, GetCasesQuery } from "../../containers/cases/types/queries.generated";
import { GET_FORM_FILTERS, GET_QUEST_FILTERS, GET_RECORD_FILTERS, GET_SIMULATION_FILTERS } from "../../containers/data/queries";
import {
	GetFormFlexEntitiesQuery,
	GetFormsFiltersQuery,
	GetQuestsFiltersQuery,
	GetQuestTableRowsQuery,
	GetRecordsFiltersQuery,
	GetRecordsQuery,
	GetSimulationsQuery,
	GetSimulationsFiltersQuery,
} from "../../containers/data/types/queries.generated";
import { acceptanceStatuses } from "../../containers/financials/documents/invoices/decisions/AcceptanceStatus";
import { getRefundStatus, handleDocumentPageFilters } from "../../containers/financials/documents/invoices/utils/filter-document-page";
import {
	DocumentRefundGroup,
	getCombinedScopeIds,
	getGroupScopes,
	getRefundGroupInfo,
} from "../../containers/financials/documents/invoices/utils/map-values";
import { GET_DOCUMENTS_FILTERS } from "../../containers/financials/documents/queries";
import { GetDocumentsFiltersQuery, GetDocumentsQuery } from "../../containers/financials/documents/types/queries.generated";
import { GET_REFUNDS_FILTERS } from "../../containers/financials/refunds/queries";
import { GetRefundsFiltersQuery, GetRefundsQuery } from "../../containers/financials/refunds/types/queries.generated";
import { GET_MODEL_FILTERS } from "../../containers/model-library/queries";
import { GetModelsFiltersQuery, GetModelsQuery, GetPublicAgreementsQuery } from "../../containers/model-library/types/queries.generated";
import { sortOrganizationDomainsLabel } from "../../containers/simulator/details/utils/sort-domains";
import { GET_USER_GLOBAL_DOMAIN_NAME } from "../../containers/simulator/queries";
import { GetUserDomainNameQuery } from "../../containers/simulator/types/queries.generated";
import { useModelsQuery } from "../../hooks/useModelsModule";
import {
	AgreementTableRowWhere,
	BaseAgreement,
	CaseStatus,
	CaseTableRowWhere,
	ContractStatus,
	DocumentStatus,
	DocumentTableRowWhere,
	FlexEntityWhere,
	FlexFormStatus,
	ModelWhere,
	PublicAgreementWhere,
	QuestCompletionStatus,
	QuestTableRowWhere,
	RecordWhere,
	RefundStatus,
	RefundTableRowWhere,
	SimulationSavedStatus,
	SimulationWhere,
} from "../../models/generated/graphql";
import { documentPageFilterFamily } from "../../store/documents";
import { organizationAgreementState, UserOrg, userOrganization } from "../../store/layout";
import { formatTreatment, notAvailableKey, notRequiredKey } from "../../utils/base-agreement-formatters";
import {
	getAdministrationOrPurchaseDateFieldName,
	getCaseOrAgreementIdFieldName,
	getPurchaseOrPatientFieldName,
} from "../../utils/getCaselessOrPatientFields";
import { maxBy } from "../../utils/max-by";
import { TrFun } from "../../utils/types";
import { transformTransactionStatusNames } from "../mapped-components/utils/status";
import { modelTagColor, ModelTagEnum } from "../model-library/ModelsContainer/ModelCard/TypeTag";
import { Badge } from "./Badge";
import { CustomOption } from "./CustomOption";
import { filterReducer } from "./FiltersRoot";
import {
	filterIcons,
	NegotiationsHistoryStatus,
	PublicAgreementStatus,
	QuestFilterStatus,
	statusBackgroundColors,
	statusColors,
	statusIcons,
} from "./mappings";
import { StatusOption } from "./StatusOption";
import { getSimulationStatusConfig } from "../../containers/simulator/components/SimulationStatus";
import { DocumentMessageModel } from "../../containers/financials/documents/messaging/models";
import { DocumentBeneficiaryEnum } from "../../containers/financials/documents/invoices/RefundInvoicePaymentFields";

export enum FilterSelectionType {
	LIST = "list",
	CHIP = "chip",
	NUMERIC_RANGE = "numeric-range",
	DATE_RANGE = "date-range",
}

export type CustomExpressionFilter<T, TWhereArgs> = {
	field: keyof T;
	getWhereExpression: (values: TWhereArgs) => any;
	isPermanent?: boolean;
	localizationKey?: string;
};

export type BaseFilterState<T = any> = {
	field: keyof T;
	isPermanent?: boolean;
	localizationKey?: string;
};

export type ListFilterOption = {
	isSelected: boolean;
	isFilteredOut?: boolean;
	value: any;
	label: string;
	nestedFilterLocalizationKey?: string;
	nested?: ListFilterState;
	count?: number;
};

export type ListFilterGetWhereExpressionArgs = { isInverted: boolean; values: Array<{ root: string; nested?: Array<string> }> };

export type ListFilterState<T = any> = (BaseFilterState<T> | CustomExpressionFilter<T, ListFilterGetWhereExpressionArgs>) & {
	selectionType: "list";
	isMulti?: boolean;
	options: Array<ListFilterOption>;
	hasMore?: boolean;
	shownItemCount?: number;
	isEmpty?: boolean;
	selectedValueCount?: number;
	hasNestedOrNested?: boolean;
	nonSearchable?: boolean;
	cannotBeInverted?: boolean;
	searchTerm?: string;
	getFirstSortingKey?: (value: any) => number | string;
	valueSequence?: Array<string>;
	label?: string;
	searchLabel?: string;
	loadMoreLabel?: string;
	renderOption?: (option: ListFilterOption, isActiveContent?: boolean) => React.ReactElement;
	isBooleanList?: boolean;
	dontHideOnMouseLeave?: boolean;
};

export type NumericFilterGetWhereExpressionArgs = { startValue?: number | null; endValue?: number | null };

export type NumericFilterState<T = any> = (BaseFilterState<T> | CustomExpressionFilter<T, NumericFilterGetWhereExpressionArgs>) & {
	selectionType: "numeric-range";
	startValue?: number | null;
	endValue?: number | null;
	min?: number | null;
	max?: number | null;
	minLabel?: string;
	maxLabel?: string;
	currencyCode?: string | null;
	rangeCounters?: Array<{ start: number; end: number; count: number }>;
	isWithoutBars?: boolean;
};

export const defaultDateRanges = ["custom", "today", "this-week", "this-month", "this-quarter", "this-year"];

export const eventDateRanges = ["custom", "last-month", "last3months", "last6months", "last12months", "lastCalYear", "y2d", "allTime"];

export type DateRangeType =
	| "custom"
	| "today"
	| "this-week"
	| "this-month"
	| "this-quarter"
	| "this-year"
	| "last-month"
	| "last3months"
	| "last6months"
	| "last12months"
	| "lastCalYear"
	| "y2d"
	| "allTime"
	| "empty";

const filterNullableValue = "IS_NULL";

const systemGeneratedKey = "system-generated";

export type DateFilterGetWhereExpressionArgs = { startValue?: string | null; endValue?: string | null };

export type DateFilterState<T = any> = (BaseFilterState<T> | CustomExpressionFilter<T, DateFilterGetWhereExpressionArgs>) & {
	selectionType: "date-range";
	rangeType: DateRangeType;
	customRanges?: Array<DateRangeType>;
	description?: string;
	isOnlyCustomAvailable?: boolean;
	startValue?: Date | null;
	endValue?: Date | null;
	min: Date | null;
	max: Date | null;
	minLabel?: string;
	maxLabel?: string;
	monthFormat?: boolean;
	typeDate?: boolean;
};

export type ChipFilterStateGetWhereExpressionArgs = { isActive?: boolean };

export type ChipFilterState<T = any> = (BaseFilterState<T> | CustomExpressionFilter<T, ChipFilterStateGetWhereExpressionArgs>) & {
	selectionType: "chip";
	isActive?: boolean;
	icons?: { iconOn: (() => JSX.Element) | undefined; iconOff: (() => JSX.Element) | undefined };
};

export type FilterListStateType<T = any> = Array<FilterStateType<T>>;

export type FilterExpressionArg = { root: any; nested?: Array<any> } | Array<{ root: any; nested?: Array<any> }>;

export type FilterGroup = {
	id: string;
	labelKey?: string;
	layout: "tabs";
	filters: FilterStateType[];
	exclusive?: boolean;
	onActiveTabChange?: (activeTabIndex: number) => void;
};

export type TableFilterAction =
	| {
			type: "select-list-value";
			path: string;
			value: string;
	  }
	| {
			type: "search-list-value";
			path: string;
			term: string;
	  }
	| {
			type: "set-boolean-value";
			path: string;
			value: boolean;
	  }
	| {
			type: "clear-selection";
			path: string;
	  }
	| {
			type: "select-current-scope";
			path: string;
	  }
	| {
			type: "set-values";
			path: string;
			startValue: Date | number | null | undefined;
			endValue: Date | number | null | undefined;
	  }
	| {
			type: "load-more";
			path: string;
	  }
	| {
			type: "set-range-type";
			path: string;
			rangeType: DateFilterState["rangeType"];
	  }
	| {
			type: "clear-all";
	  }
	| {
			type: "restore-state";
			state: FilterListStateType;
	  }
	| {
			type: "show-less";
			path: string;
	  };

export type FilterStateType<T = any> =
	| ListFilterState<T>
	| NumericFilterState<T>
	| DateFilterState<T>
	| ChipFilterState<T>;

export const initiallyShownOptionsCount = 6;

export const loadMorePortionSize = 100;

export const filterDialogVirtualScrollThresholdItems = 300;

export const isFilterEmpty = (state: FilterStateType) => {
	switch (state.selectionType) {
		case "list":
			return !!state.isEmpty;

		case "numeric-range":
			return (
				(isEmptyValue(state.startValue) || state.startValue === state.min) &&
				(isEmptyValue(state.endValue) || state.endValue === state.max)
			);

		case "date-range":
			return isEmptyValue(state.startValue) && isEmptyValue(state.endValue) && state.rangeType === "custom";

		case "chip": {
			return !state.isActive;
		}
	}
};

export const getFilterValues = (state: FilterStateType, isInverted = false) => {
	switch (state.selectionType) {
		case "list":
			return state.options
				.filter((option) => option.isSelected !== isInverted)
				.map((option) => [
					option.value,
					option.nested?.options
						.filter((nestedOption) => nestedOption.isSelected !== isInverted)
						.map((nestedOption) => nestedOption.value),
				]);

		case "numeric-range":
			return [state.startValue, state.endValue];

		case "date-range":
			return [
				state.rangeType,
				state.startValue && format(state.startValue, defaultDateFormat),
				state.endValue && format(state.endValue, defaultDateFormat),
			];

		case "chip":
			return [state.isActive];
	}
};

export const calculateSelectedListOptionCount = (state: ListFilterState) => {
	return state.options
		.filter((option) => option.isSelected)
		.map((option) => 1 + (option.nested?.options.filter((nestedOption) => nestedOption.isSelected).slice(1).length ?? 0))
		.reduce((a, c) => a + c, 0);
};

export const getSelectedValueCount = (state: FilterStateType) => {
	if (state.selectionType === "list") {
		return state.selectedValueCount || 0;
	}

	return isFilterEmpty(state) ? 0 : 1;
};

export const setFilterValues = (state: FilterStateType, values: Array<any>, searchTerm?: string) => {
	if (state.selectionType === "list") {
		const listFilterState: ListFilterState = sortFilterOptionsIfNeeded({
			...state,
			searchTerm,
			options: state.options.map((option) => {
				const sourceValues = values.find(([root]) => option.value === root);

				return {
					...option,
					isSelected: !!sourceValues,
					isFilteredOut: !!searchTerm && !option.label.toLowerCase().includes(searchTerm.toLowerCase()),
					nested:
						option.nested && sourceValues
							? sortFilterOptionsIfNeeded({
									...option.nested,
									options: option.nested.options.map((nestedOption) => ({
										...nestedOption,
										isSelected: sourceValues[1]?.includes(nestedOption.value),
									})),
							  } as ListFilterState<any>)
							: option.nested,
				};
			}),
		});

		return {
			...listFilterState,
			isEmpty: listFilterState.options.every((option) => !option.isSelected),
			selectedValueCount: calculateSelectedListOptionCount(listFilterState),
		} as ListFilterState;
	}

	if (state.selectionType === "numeric-range") {
		return {
			...state,
			startValue: values[0],
			endValue: values[1],
		} as NumericFilterState;
	}

	if (state.selectionType === "date-range") {
		return {
			...state,
			rangeType: values[0] ?? "custom",
			startValue: values[1] && parse(values[1], "yyyy-MM-dd", new Date()),
			endValue: values[2] && parse(values[2], "yyyy-MM-dd", new Date()),
		} as DateFilterState;
	}

	if (state.selectionType === "chip") {
		return {
			...state,
			isActive: values[0],
		};
	}

	return state;
};

export function getDateFilterRange(filter: DateFilterState) {
	switch (filter.rangeType) {
		case "custom":
			return filter.typeDate
				? [filter.startValue && startOfDay(filter.startValue), filter.endValue && endOfDay(filter.endValue)]
				: [
						filter.startValue && format(filter.startValue, defaultDateFormat),
						filter.endValue && format(filter.endValue, defaultDateFormat),
				  ];

		case "today":
			return [format(startOfDay(new Date()), defaultDateFormat)];

		case "this-week":
			return [format(startOfWeek(new Date()), defaultDateFormat)];

		case "this-month":
			return [format(startOfMonth(new Date()), defaultDateFormat)];

		case "this-quarter":
			return [format(startOfQuarter(new Date()), defaultDateFormat)];

		case "this-year":
		case "y2d":
			return [format(startOfYear(new Date()), defaultDateFormat)];

		case "last-month":
			return [
				format(startOfMonth(addMonths(new Date(), -1)), defaultDateFormat),
				format(endOfMonth(addMonths(new Date(), -1)), defaultDateFormat),
			];

		case "last3months":
			return [
				format(startOfMonth(addMonths(new Date(), -3)), defaultDateFormat),
				format(endOfMonth(addMonths(new Date(), -1)), defaultDateFormat),
			];

		case "last6months":
			return [
				format(startOfMonth(addMonths(new Date(), -6)), defaultDateFormat),
				format(endOfMonth(addMonths(new Date(), -1)), defaultDateFormat),
			];

		case "last12months":
			return [
				format(startOfMonth(addMonths(new Date(), -12)), defaultDateFormat),
				format(endOfMonth(addMonths(new Date(), -1)), defaultDateFormat),
			];

		case "lastCalYear":
			return [
				format(startOfYear(addYears(new Date(), -1)), defaultDateFormat),
				format(endOfYear(addYears(new Date(), -1)), defaultDateFormat),
			];

		case "empty":
			return [filterNullableValue];

		default:
			return [];
	}
}

function shouldInvertSelection(state: ListFilterState) {
	if (state.cannotBeInverted) {
		return false;
	}

	const selectedOptions = state.options.filter((option) => option.isSelected);

	return selectedOptions.length > state.options.length / 2;
}

function getWhereExpressionArgs(state: FilterStateType) {
	switch (state.selectionType) {
		case "list": {
			const isInverted = shouldInvertSelection(state);

			return {
				isInverted,
				values: (getFilterValues(state, isInverted) as Array<Array<any>>).map(([root, nested]) => ({ root, nested })),
			};
		}
		case "numeric-range":
			return { startValue: state.startValue, endValue: state.endValue };

		case "date-range": {
			const [startValue, endValue] = getDateFilterRange(state);

			return {
				startValue,
				endValue,
			};
		}

		case "chip":
			return { isActive: state.isActive };
	}
}

function escapeRegex(str: string) {
	return str.replace(/[.*+?^${}()|[\]\\\/]/g, "\\$&");
}

export const getWhereExpression = (state: FilterListStateType) => {
	const propertyConditions: Array<FilterExpressionArg> = state
		.filter((filter) => !isFilterEmpty(filter))
		.map((filter) => {
			if ((filter as CustomExpressionFilter<any, any>).getWhereExpression) {
				const args = getWhereExpressionArgs(filter);

				return (filter as CustomExpressionFilter<any, any>).getWhereExpression(args);
			}

			switch (filter.selectionType) {
				case "list": {
					const isInverted = shouldInvertSelection(filter);
					if (isInverted) {
						return {
							NOT: {
								[`${filter.field as string}_IN`]: filter.options
									.filter((option) => !option.isSelected)
									.map(({ value }) => value),
							},
						};
					}

					return filter.isMulti
						? {
								[`${filter.field as string}_IN`]: filter.options
									.filter((option) => option.isSelected)
									.map(({ value }) => value),
						  }
						: { [filter.field as string]: filter.options.find((option) => option.isSelected)?.value };
				}
				case "numeric-range":
					return Object.fromEntries(
						Object.entries({
							[`${filter.field as string}_GTE`]: filter.startValue,
							[`${filter.field as string}_LTE`]: filter.endValue,
						}).filter(([_, value]) => value)
					);

				case "date-range": {
					const range = getDateFilterRange(filter);

					return Object.fromEntries(
						Object.entries({
							[`${filter.field as string}_GTE`]: range[0],
							[`${filter.field as string}_LTE`]: range[1],
						}).filter(([_, value]) => value)
					);
				}

				case "chip":
					return { [filter.field as string]: filter };
			}
		});

	return propertyConditions.length ? { AND: propertyConditions } : {};
};

const setLoadedFilterValues = (state: FilterListStateType, storedData: any) => {
	const storedObj = typeof storedData === "string" ? JSON.parse(storedData) : storedData || {};

	return state.map((filter) => (storedObj[filter.field] ? setFilterValues(filter, storedObj[filter.field]) : filter));
};

const createNumericInitialValues = (amountRanges: Array<{ start: number; end: number }>) => {
	const min = amountRanges.length ? Math.min(...amountRanges.map(({ start }) => start)) : 0;
	const max = amountRanges.length ? Math.max(...amountRanges.map(({ end }) => end)) : 0;

	return {
		min,
		max,
		startValue: min,
		endValue: max,
	};
};

const optionSort = <T>(filter: ListFilterState<T>) =>
	sortByComparisons<ListFilterOption>(
		byDesc((x) => x.isSelected),
		byAsc((x) => (filter.getFirstSortingKey ? filter.getFirstSortingKey(x.value) : 0)),
		byAsc((x) => x.label)
	);

const setInitialListStates = <T>(state: FilterListStateType<T>): FilterListStateType<T> =>
	state.map((filter) =>
		filter.selectionType === "list"
			? {
					...filter,
					options: filter.valueSequence
						? filter.isBooleanList
							? filter.options.sort((a, b) => {
									return a.label === NegotiationsHistoryStatus.YES ? -1 : 1;
							  })
							: filter.options.sort(
									sortBySelectors(
										(option) => filter.valueSequence!.indexOf(option.value),
										(option) => option.label
									)
							  )
						: filter.options.sort(optionSort(filter)),
					selectedValueCount: 0,
					isEmpty: filter.options.every((option) => !option.isSelected),
					hasNestedOrNested: filter.options.some((option) => option.nested),
			  }
			: filter
	);

export const dashboardTimePeriod = "timePeriod";
export const dashboardInvoiceTimePeriod = "invoiceTimePeriod";

export const toOptions = (values: Array<string>): Array<ListFilterOption> =>
	values.map((value) => ({ label: value, value, isSelected: false }));

export const sortFilterOptionsIfNeeded = <T>(filter: ListFilterState<T>): ListFilterState<T> =>
	filter.valueSequence
		? filter
		: {
				...filter,
				options: filter.options.sort(optionSort(filter)),
		  };

const fromFilterToValueList = (filter: FilterStateType) => {
	switch (filter.selectionType) {
		case "list":
			return filter.options
				.map((option) => (option.isSelected ? [option.value] : [undefined]))
				.filter((option): option is Array<string> => Boolean(option[0]));

		case "numeric-range":
			return [filter.startValue, filter.endValue];

		case "date-range":
			return [
				filter.rangeType ?? "custom",
				filter.startValue && format(parseDate(filter.startValue), defaultDateFormat),
				filter.endValue && format(parseDate(filter.endValue), defaultDateFormat),
			];

		case "chip":
			return filter.isActive ? [[filter.field]] : [];
	}

	return [];
};

export const setLocalFilterValues = (state: FilterListStateType, storedState: FilterListStateType) => {
	return state.map((filter) => {
		const storedFilter = storedState.find((storedFilter) => storedFilter.field === filter.field);
		if (storedFilter) {
			return setFilterValues(filter, fromFilterToValueList(storedFilter), (storedFilter as ListFilterState)?.searchTerm);
		}

		return filter;
	});
};

export const mapAgreementsTableFilterData = (
	data: GetAgreementsFilters_GetAgreementsTableSourceFilterData,
	t: TrFun,
	organization: UserOrg,
	isDashboard: boolean
): FilterListStateType => {
	const dashboardFilters = isDashboard
		? ([
				{
					selectionType: "date-range",
					field: dashboardTimePeriod,
					customRanges: eventDateRanges,
					description: "time-period-description",
					localizationKey: "time-period",
					isPermanent: true,
					rangeType: "custom",
				},
				{
					selectionType: "date-range",
					field: dashboardInvoiceTimePeriod,
					customRanges: eventDateRanges,
					description: "time-period-invoice-description",
					localizationKey: "time-period-invoice",
					isPermanent: true,
					rangeType: "custom",
				},
		  ] as FilterListStateType<
				GetAgreementsQuery["agreementTableRows"][number] & { [dashboardTimePeriod]: string } & {
					[dashboardInvoiceTimePeriod]: string;
				}
		  >)
		: [];

	const tagsFilter: FilterListStateType<GetAgreementsQuery["agreementTableRows"][number]> =
		organization.agreementTagNames.length > 1
			? [
					{
						selectionType: "list",
						field: "agreementTagTypes",
						localizationKey: "tags",
						getWhereExpression: ({ values }): AgreementTableRowWhere => ({
							AND: values.map(({ root }) => ({ agreementTagTypes_INCLUDES: root })),
						}),
						options: toOptions(data.tags),
						renderOption: (option, isActiveContent) =>
							Badge({
								isActive: option.isSelected,
								name: t(option.label),
								isActiveContent,
							}),
						cannotBeInverted: true,
						isPermanent: true,
						isMulti: true,
						nonSearchable: true,
					} as ListFilterState<GetAgreementsQuery["agreementTableRows"][number]>,
			  ]
			: [];

	const agreementStatuses: Array<ContractStatus> = [
		ContractStatus.InApproval,
		ContractStatus.Approved,
		ContractStatus.Rejected,
		ContractStatus.Running,
	];

	return setInitialListStates<
		GetAgreementsQuery["agreementTableRows"][number] & { [dashboardTimePeriod]: string } & { [dashboardInvoiceTimePeriod]: string }
	>([
		...dashboardFilters,
		...tagsFilter,
		{
			selectionType: "list",
			field: "formattedCounterparties",
			localizationKey: "counterparties",
			getWhereExpression: ({ isInverted, values }): AgreementTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { formattedCounterparties_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ formattedCounterparties_CONTAINS: root })) },
			options: toOptions(data.counterparties),
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "name",
			options: toOptions(data.names),
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "formattedDiseases",
			localizationKey: "disease",
			getFirstSortingKey: (value) => (value === t(notRequiredKey) || value === t(notAvailableKey) ? 1 : -1),
			getWhereExpression: ({ isInverted, values }): AgreementTableRowWhere =>
				isInverted
					? {
							AND: values.map(({ root, nested }) => ({
								NOT: {
									formattedDiseases_CONTAINS: root,
									AND: nested?.map((nestedValue) => ({ NOT: { formattedIndications_CONTAINS: nestedValue } })),
								},
							})),
					  }
					: {
							OR: values.map(({ root, nested }) => ({
								formattedDiseases_CONTAINS: root,
								OR: nested?.map((nestedValue) => ({ formattedIndications_CONTAINS: nestedValue })),
							})),
					  },
			options: data.diseases.map((diseaseIndications) => ({
				label: diseaseIndications.name,
				value: diseaseIndications.name,
				isSelected: false,
				nested: {
					selectionType: "list",
					field: "indication" as any,
					hasNestedOrNested: true,
					options: diseaseIndications.indications.map((indication) => ({
						label: indication,
						value: indication,
						isSelected: false,
					})),
					getFirstSortingKey: (value) => (value === t(notRequiredKey) || value === t(notAvailableKey) ? 1 : -1),
					isMulti: true,
					searchLabel: "search-indications-for",
					loadMoreLabel: "load-more-indications-for",
				},
			})),
			isMulti: true,
			isPermanent: true,
			searchLabel: "search-diseases",
		},
		{
			selectionType: "list",
			field: "formattedProducts",
			localizationKey: "product",
			getWhereExpression: ({ isInverted, values }): AgreementTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { formattedProducts_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ formattedProducts_CONTAINS: root })) },
			options: toOptions(data.brandNames),
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "numeric-range",
			field: "totalAmount",
			minLabel: "min-agreement-amount",
			maxLabel: "max-agreement-amount",
			currencyCode: organization.currencyCodes?.[0]?.Code,
			rangeCounters: data.amountRanges,
			isPermanent: isDashboard,
			...createNumericInitialValues(data.amountRanges),
		},
		{
			selectionType: "numeric-range",
			field: "daysToExpiry",
			minLabel: "min-agreement-days-to-expire",
			maxLabel: "max-agreement-days-to-expire",
			rangeCounters: data.daysToExpiryRanges,
			isPermanent: isDashboard,
			...createNumericInitialValues(data.daysToExpiryRanges),
		},
		{
			selectionType: "date-range",
			field: "validFromDate",
			localizationKey: "validity-period",
			rangeType: "custom",
			min: data.minValidFromDate,
			max: data.maxValidFromDate,
			minLabel: "valid-from",
			maxLabel: "valid-to",
		},
		{
			selectionType: "list",
			field: "status",
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: option.label,
					color: statusColors[option.value as keyof typeof statusColors],
					backgroundColor: statusBackgroundColors[option.value as keyof typeof statusBackgroundColors],
					icon: statusIcons[option.value as keyof typeof statusIcons],
				}),
			valueSequence: agreementStatuses,
			options: agreementStatuses.map((status) => ({ value: status, label: t(status), isSelected: false })),
			nonSearchable: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "id",
			isMulti: true,
			localizationKey: "agreement-id",
			options: toOptions(data.ids),
		},
	]);
};

export const mapPublicAgreementsTableFilterData = (
	data: GetPublicAgreementsFiltersQuery["GetPublicAgreementsSourceFilterData"],
	t: TrFun
): FilterListStateType => {
	const agreementStatuses: Array<PublicAgreementStatus> = [PublicAgreementStatus.ACTIVE, PublicAgreementStatus.INACTIVE];
	const renegotiations: Array<string> = [NegotiationsHistoryStatus.YES, NegotiationsHistoryStatus.NO];
	const localizedUnknownValue = "unknown";

	return setInitialListStates<
		NonNullable<GetPublicAgreementsQuery["GetPublicAgreements"]["displayedPublicAgreements"]>[number] & { region: any }
	>([
		{
			selectionType: "list",
			field: "product",
			isMulti: true,
			isPermanent: true,
			cannotBeInverted: true,
			options: toOptions(data.products),
		},
		{
			selectionType: "list",
			field: "formattedManufacturers",
			localizationKey: "manufacturer",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): PublicAgreementWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { manufacturers_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ manufacturers_INCLUDES: root })) },
			isMulti: true,
			isPermanent: true,
			options: toOptions(data.manufacturers),
		},
		{
			selectionType: "list",
			field: "payer",
			cannotBeInverted: true,
			isMulti: true,
			isPermanent: true,
			options: toOptions(data.payers),
		},
		{
			selectionType: "list",
			field: "hta",
			localizationKey: "hta",
			cannotBeInverted: true,
			renderOption: (option) => {
				return CustomOption({ name: option.label, isActive: option.isSelected, localizedOption: localizedUnknownValue });
			},
			getWhereExpression: ({ isInverted, values }): PublicAgreementWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { hta_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ hta_INCLUDES: root })) },
			isMulti: true,
			isPermanent: true,
			options: toOptions(data.hta?.filter((hta) => hta !== null) as string[]),
		},
		{
			selectionType: "list",
			field: "country",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): PublicAgreementWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { country: { name: root } } })) }
					: { OR: values.map(({ root }) => ({ country: { name: root } })) },
			isMulti: true,
			isPermanent: true,
			options: toOptions(data.countries),
		},
		{
			selectionType: "list",
			field: "models",
			localizationKey: "model",
			isMulti: true,
			isPermanent: true,
			cannotBeInverted: true,
			renderOption: (option) => {
				return CustomOption({ name: option.label, isActive: option.isSelected, localizedOption: localizedUnknownValue });
			},
			getWhereExpression: ({ isInverted, values }): PublicAgreementWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { models_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ models_INCLUDES: root })) },
			options: toOptions(data.models),
		},
		{
			selectionType: "list",
			field: "diseaseNames",
			localizationKey: "disease",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): PublicAgreementWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { diseaseNames_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ diseaseNames_INCLUDES: root })) },
			isMulti: true,
			isPermanent: true,
			options: toOptions(data.diseases),
		},
		{
			selectionType: "list",
			field: "therapeuticAreas",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): PublicAgreementWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { therapeuticAreas_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ therapeuticAreas_INCLUDES: root })) },
			localizationKey: "therapeutic-area",
			isMulti: true,
			options: toOptions(data.therapeuticAreas),
		},
		{
			selectionType: "list",
			field: "productType",
			cannotBeInverted: true,
			localizationKey: "product-type",
			isMulti: true,
			options: toOptions(data.productTypes),
		},
		{
			selectionType: "list",
			localizationKey: "mechanism-of-action",
			field: "mechanismOfAction",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): PublicAgreementWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { mechanismOfAction_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ mechanismOfAction_INCLUDES: root })) },
			isMulti: true,
			renderOption: (option) => {
				return CustomOption({ name: option.label, isActive: option.isSelected, localizedOption: localizedUnknownValue });
			},
			options: toOptions(data.mechanismsOfAction),
		},
		{
			selectionType: "list",
			field: "measures",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): PublicAgreementWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { measures_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ measures_INCLUDES: root })) },
			isMulti: true,
			options: toOptions(data.measures),
		},
		{
			selectionType: "list",
			field: "uncertainties",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }) =>
				isInverted
					? { AND: values.map(({ root }) => ({ uncertainties: { NOT: { name: root } } })) }
					: { OR: values.map(({ root }) => ({ uncertainties: { name: root } })) },
			renderOption: (option) => {
				return CustomOption({ name: option.label, isActive: option.isSelected, localizedOption: localizedUnknownValue });
			},
			isMulti: true,
			options: toOptions(data.uncertainties),
		},
		{
			selectionType: "list",
			field: "region",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): PublicAgreementWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ country: { NOT: { region: root } } })) }
					: { OR: values.map(({ root }) => ({ country: { region: root } })) },
			isMulti: true,
			options: toOptions(data.regions),
		},
		{
			selectionType: "list",
			field: "year",
			isMulti: true,
			cannotBeInverted: true,
			options: data.years.map((year) => ({ label: `${year}`, value: year, isSelected: false })),
		},
		{
			selectionType: "list",
			localizationKey: "negotiation-history",
			field: "hasRenegotiations",
			cannotBeInverted: true,
			renderOption: (option) => {
				return CustomOption({
					isActive: option.isSelected,
					name: option.label,
				});
			},
			valueSequence: renegotiations,
			options: renegotiations.map((status) => ({
				value: status === NegotiationsHistoryStatus.YES,
				label: t(status),
				isSelected: false,
			})),
			nonSearchable: true,
			isBooleanList: true,
		},
		{
			selectionType: "list",
			field: "status",
			cannotBeInverted: true,
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: option.label,
					color: statusColors[option.value as keyof typeof statusColors],
					backgroundColor: statusBackgroundColors[option.value as keyof typeof statusBackgroundColors],
					icon: statusIcons[option.value as keyof typeof statusIcons],
				}),
			valueSequence: agreementStatuses,
			options: agreementStatuses.map((status) => ({ value: status, label: t(status), isSelected: false })),
			nonSearchable: true,
		},
	]);
};

export const mapModelsTableFilterData = (data: GetModelsFiltersQuery["GetModelsSourceFilterData"], t: TrFun): FilterListStateType => {
	return setInitialListStates<
		GetModelsQuery["GetModels"]["models"][number] & {
			favorites: any;
		}
	>([
		{
			selectionType: "chip",
			field: "favorites",

			getWhereExpression: ({ isActive }: ChipFilterStateGetWhereExpressionArgs): ModelWhere => ({ isFavorite: isActive }),
			localizationKey: "favorites",
			isPermanent: true,

			icons: { iconOn: filterIcons["FavoriteIcon"], iconOff: filterIcons["FavoriteBorderIcon"] },
		},
		{
			selectionType: "list",
			field: "type",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): ModelWhere =>
				isInverted ? { NOT: { type_IN: values.map(({ root }) => root) } } : { type_IN: values.map(({ root }) => root) },
			isPermanent: true,
			isMulti: true,
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: option.label,
					color: modelTagColor[option.value as ModelTagEnum]?.color,
					backgroundColor: modelTagColor[option.value as ModelTagEnum]?.backgroundColor,
				}),
			options: toOptions(data.types),
		},
		{
			selectionType: "list",
			field: "challenges",
			isMulti: true,
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): ModelWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { challenges_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ challenges_INCLUDES: root })) },
			isPermanent: true,
			options: toOptions(data.challenges),
		},
		{
			selectionType: "list",
			field: "measures",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): ModelWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { measures_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ measures_INCLUDES: root })) },
			isMulti: true,
			isPermanent: true,
			options: toOptions(data.measures),
		},
		{
			selectionType: "list",
			field: "dataTypes",
			localizationKey: "data-types",
			isMulti: true,
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): ModelWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { dataTypes_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ dataTypes_INCLUDES: root })) },
			options: toOptions(data.dataTypes),
		},
		{
			selectionType: "list",
			field: "therapeuticAreas",

			cannotBeInverted: true,
			localizationKey: "therapeutic-area",
			getWhereExpression: ({ isInverted, values }): ModelWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { therapeuticAreas_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ therapeuticAreas_INCLUDES: root })) },
			isMulti: true,
			options: toOptions(data.therapeuticAreas),
		},
		{
			selectionType: "list",
			field: "scopes",
			localizationKey: "scope",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): ModelWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { scopes_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ scopes_INCLUDES: root })) },
			isMulti: true,
			options: toOptions(data.scopes),
		},
		{
			selectionType: "list",
			field: "financialFlows",
			cannotBeInverted: true,
			localizationKey: "settlement-terms",
			getWhereExpression: ({ isInverted, values }): ModelWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { financialFlows_INCLUDES: root } })) }
					: { OR: values.map(({ root }) => ({ financialFlows_INCLUDES: root })) },
			isMulti: true,
			options: toOptions(data.financialFlows),
		},
	]);
};

export const mapCasesTableFilterData = (
	data: GetCasesFiltersQuery["GetCasesTableSourceFilterData"],
	t: TrFun,
	organization: UserOrg
): FilterListStateType => {
	const getNestedStatusFilter = (status: CaseStatus): ListFilterState | undefined => {
		if (status === CaseStatus.InReview) {
			return {
				selectionType: "list",
				field: "counterpartyInReview",
				searchLabel: "search-reviewers",
				isMulti: true,
				label: "in-review-case-by",
				hasNestedOrNested: true,
				options: toOptions(data.inReviewByNames),
			};
		}

		if (status === CaseStatus.Suspended) {
			return {
				selectionType: "list",
				field: "suspensionReason",
				searchLabel: "search-reasons-for-suspension",
				isMulti: true,
				hasNestedOrNested: true,
				label: "suspended-case-due-to",
				options: data.suspensionReasons.map((reason) => ({ label: t(reason), value: reason, isSelected: false })),
			};
		}

		return undefined;
	};

	const getStatusNestedFilterExpression = (status: CaseStatus, nestedValues?: Array<string>) => {
		if (status === CaseStatus.InReview) {
			return nestedValues?.length ? { counterpartyInReview_IN: nestedValues } : {};
		}

		if (status === CaseStatus.Suspended) {
			return nestedValues?.length ? { suspensionReason_IN: nestedValues } : {};
		}

		return undefined;
	};

	const caseStatuses: Array<CaseStatus> = [CaseStatus.InReview, CaseStatus.Tentative, CaseStatus.Active, CaseStatus.Suspended];

	return setInitialListStates<GetCasesQuery["caseTableRows"][number]>([
		{
			selectionType: "list",
			field: "patientInsuranceId",
			localizationKey: "patientId",
			options: toOptions(data.patientIds),
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "agreementName",
			localizationKey: "agreement-name",
			options: toOptions(data.agreementNames),
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "preparationBrandName",
			localizationKey: "product",
			getWhereExpression: ({ isInverted, values }): CaseTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { preparationBrandName_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ preparationBrandName_CONTAINS: root })) },
			options: toOptions(data.brandNames),
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "status",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): CaseTableRowWhere => ({
				OR: values.map(({ root, nested }) => ({
					status: root as CaseStatus,
					...getStatusNestedFilterExpression(root as CaseStatus, nested),
				})),
			}),
			valueSequence: caseStatuses,
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: option.label,
					color: statusColors[option.value as keyof typeof statusColors],
					backgroundColor: statusBackgroundColors[option.value as keyof typeof statusBackgroundColors],
					icon: statusIcons[option.value as keyof typeof statusIcons],
				}),
			options: caseStatuses.map((status) => ({
				value: status,
				label: t(status),
				isSelected: false,
				nested: getNestedStatusFilter(status as CaseStatus),
			})),
			isPermanent: true,
			nonSearchable: true,
			isMulti: true,
		} as ListFilterState<GetCasesQuery["caseTableRows"][number]>,
		{
			selectionType: "numeric-range",
			field: "totalAmount",
			currencyCode: organization.currencyCodes?.[0]?.Code,
			rangeCounters: data.amountRanges,
			...createNumericInitialValues(data.amountRanges),
		},
		{
			selectionType: "list",
			field: "patientInitials",
			localizationKey: "patient-initials",
			options: toOptions(data.initials),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "id",
			localizationKey: "case-id",
			options: toOptions(data.ids),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "formattedIndications",
			localizationKey: "indication",
			options: toOptions(data.indicationCodes),
			isMulti: true,
			getWhereExpression: ({ isInverted, values }): CaseTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { formattedIndications_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ formattedIndications_CONTAINS: root })) },
		},
	]);
};

const getSimulationStatus = (status: SimulationSavedStatus, t: TrFun) => {
	switch (status) {
		case SimulationSavedStatus.Draft:
			return t("simulation-state-draft");
		case SimulationSavedStatus.Complete:
			return t("simulation-state-complete");
		default:
			return t("simulation-state-complete");
	}
};

export const mapSimulationTableFilterData = (data: GetSimulationsFiltersQuery["GetSimulationsTableSourceFilterData"], t: TrFun) => {
	const SimulationStatusConfig = getSimulationStatusConfig(t);

	return setInitialListStates<GetSimulationsQuery["simulations"][number]>([
		{
			selectionType: "list",
			field: "model",
			getWhereExpression: ({ isInverted, values }): SimulationWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { model_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ model_CONTAINS: root })) },
			options: toOptions(data.models),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "shortName",
			localizationKey: "short-name",
			getWhereExpression: ({ isInverted, values }): SimulationWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { shortName_MATCHES: escapeRegex(root) } })) }
					: { OR: values.map(({ root }) => ({ shortName_MATCHES: escapeRegex(root) })) },
			options: toOptions(data.shortNames),
			isMulti: true,
			isPermanent: false,
		},
		{
			selectionType: "list",
			field: "product",
			options: toOptions(data.products),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			// ensure field matches the filter path in FiltersRoot.tsx
			field: "domainName",
			getWhereExpression: ({ isInverted, values }): SimulationWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { domainName_MATCHES: escapeRegex(root) } })) }
					: { OR: values.map(({ root }) => ({ domainName_MATCHES: escapeRegex(root) })) },
			options: toOptions(data.domains),
			localizationKey: "domain-header-general",
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "counterparty",
			getWhereExpression: ({ isInverted, values }): SimulationWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { counterparty_MATCHES: escapeRegex(root) } })) }
					: { OR: values.map(({ root }) => ({ counterparty_MATCHES: escapeRegex(root) })) },
			options: toOptions(data.counterparties),
			localizationKey: "simulation-counterparty",
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "createdByName",
			options: toOptions(data.addedByNames),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "status",
			localizationKey: "status",
			isMulti: false,
			isPermanent: true,
			options: data.statuses.map((status) => ({ value: status, label: getSimulationStatus(status, t), isSelected: false })),
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: SimulationStatusConfig[option.value as keyof typeof SimulationStatusConfig].label,

					color: SimulationStatusConfig[option.value as keyof typeof SimulationStatusConfig].color,
					backgroundColor: SimulationStatusConfig[option.value as keyof typeof SimulationStatusConfig].backgroundColor,
					icon: () => SimulationStatusConfig[option.value as keyof typeof SimulationStatusConfig].icon,
				}),
		},
		{
			selectionType: "date-range",
			field: "lastUpdatedOn",
			rangeType: "custom",
			localizationKey: "last-updated",
			typeDate: true,
			min: data.lastUpdatedOn,
			max: data.lastUpdatedOn,
		},
		{
			selectionType: "list",
			field: "id",
			localizationKey: "simulation-id",
			options: toOptions(data.ids),
			isMulti: true,
		},
	]);
};

export const mapFormsTableFilterData = (data: GetFormsFiltersQuery["GetFormsTableSourceFilterData"], t: TrFun) => {
	const formStatuses: Array<FlexFormStatus> = [FlexFormStatus.Draft, FlexFormStatus.Submitted];

	return setInitialListStates<GetFormFlexEntitiesQuery["flexEntities"][number]>([
		{
			selectionType: "list",
			field: "patientId",
			options: toOptions(data.patientIds),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "preparationBrandNames",
			localizationKey: "product",
			getWhereExpression: ({ isInverted, values }): FlexEntityWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { preparationBrandNames_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ preparationBrandNames_CONTAINS: root })) },
			options: toOptions(data.brandNames),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "status",
			valueSequence: formStatuses,
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: option.label,
					color: statusColors[option.value as keyof typeof statusColors],
					backgroundColor: statusBackgroundColors[option.value as keyof typeof statusBackgroundColors],
					icon: statusIcons[option.value as keyof typeof statusIcons],
				}),
			options: formStatuses.map((status) => ({ value: status, label: t(status), isSelected: false })),
			isPermanent: true,
			nonSearchable: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "viewCreatedByName",
			localizationKey: "createdByName",
			options: data.addedByNames.map((value) => ({
				label: value === systemGeneratedKey ? t(systemGeneratedKey) : value,
				value,
				isSelected: false,
			})),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "date-range",
			field: "createdOn",
			rangeType: "custom",
			localizationKey: "added-on",
			min: data.minAddedOn,
			max: data.maxAddedOn,
		},
		{
			selectionType: "list",
			field: "flexEntityName",
			localizationKey: "type",
			getWhereExpression: ({ isInverted, values }): FlexEntityWhere =>
				isInverted
					? { NOT: { flexEntityName_IN: values.map(({ root }) => root) } }
					: { flexEntityName_IN: values.map(({ root }) => root) },
			options: data.types.map((formType) => ({ value: formType, label: t(formType), isSelected: false })),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "decisionTherapy",
			options: data.therapyDecisions.map((therapyDecision) => ({
				value: therapyDecision,
				label: t(`decisions-${therapyDecision}`),
				isSelected: false,
			})),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "occurrence",
			options: data.occurrences.map((occurrence) => ({ value: occurrence, label: t(occurrence), isSelected: false })),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "id",
			localizationKey: "form-id",
			options: toOptions(data.ids),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "externalFormId",
			options: toOptions(data.externalFormIds),
			isMulti: true,
		},
	]);
};

export const mapRecordsTableFilterData = (data: GetRecordsFiltersQuery, t: TrFun) => {
	const recordStatuses: Array<FlexFormStatus> = [FlexFormStatus.Unmatched, FlexFormStatus.Matched, FlexFormStatus.Revoked];
	const filterData = data.GetRecordsTableSourceFilterData;
	const hasOnlyPatientAgreements = data?.GetOrganizationCaselessAgreementStatus.hasOnlyPatientAgreements || false;
	const hasOnlyCaselessAgreements = data?.GetOrganizationCaselessAgreementStatus.hasOnlyCaselessAgreements || false;

	return setInitialListStates<GetRecordsQuery["records"][number] & { purchaseOrPatientId: string }>([
		{
			selectionType: "list",
			field: "purchaseOrPatientId",
			localizationKey: getPurchaseOrPatientFieldName(hasOnlyPatientAgreements, hasOnlyCaselessAgreements, t),
			options: toOptions(filterData.purchaseOrPatientIds),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "preparationBrandNames",
			localizationKey: "product",
			getWhereExpression: ({ isInverted, values }): RecordWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { preparationBrandNames_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ preparationBrandNames_CONTAINS: root })) },
			options: toOptions(filterData.brandNames),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "status",
			valueSequence: recordStatuses,
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: option.label,
					color: statusColors[option.value as keyof typeof statusColors],
					backgroundColor: statusBackgroundColors[option.value as keyof typeof statusBackgroundColors],
					icon: statusIcons[option.value as keyof typeof statusIcons],
				}),
			options: recordStatuses.map((status) => ({ value: status, label: t(status), isSelected: false })),
			isPermanent: true,
			nonSearchable: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "createdByName",
			options: toOptions(filterData.addedByNames),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "date-range",
			field: "createdOn",
			rangeType: "custom",
			localizationKey: "added-on",
			min: filterData.minAddedOn,
			max: filterData.maxAddedOn,
		},
		{
			selectionType: "date-range",
			field: "viewDate",
			rangeType: "custom",
			localizationKey: "record date",
			min: filterData.minRecordDate,
			max: filterData.maxRecordDate,
		},
		{
			selectionType: "list",
			field: "viewCases",
			localizationKey: "cases",
			options: toOptions(filterData.caseIds),
			isMulti: true,
			getWhereExpression: ({ isInverted, values }): RecordWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { viewCases_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ viewCases_CONTAINS: root })) },
		},
		{
			selectionType: "list",
			field: "type",
			options: filterData.types.map((formType) => ({ value: formType, label: t(formType), isSelected: false })),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "occurrence",
			options: filterData.occurrences.map((occurrence) => ({ value: occurrence, label: t(occurrence), isSelected: false })),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "id",
			localizationKey: "record-id",
			options: toOptions(filterData.ids),
			isMulti: true,
		},
	]);
};

export const mapQuestsTableFilterData = (
	data: GetQuestsFiltersQuery["GetQuestsTableSourceFilterData"],
	t: TrFun,
	organization: UserOrg
) => {
	const recordStatuses: Array<FlexFormStatus> = [FlexFormStatus.Matched, FlexFormStatus.Unmatched, FlexFormStatus.Revoked];
	const questStatuses: Array<QuestFilterStatus> = [QuestFilterStatus.UNRESOLVED, QuestFilterStatus.RESOLVED];

	return setInitialListStates<
		GetQuestTableRowsQuery["questTableRows"][number] & {
			patientId: unknown;
			administrationDate: unknown;
			quantity: unknown;
			recordStatus: unknown;
			recordId: unknown;
		}
	>([
		{
			selectionType: "list",
			field: "brandName",
			localizationKey: "product",
			options: toOptions(data.brandNames),
			getWhereExpression: ({ isInverted, values }): QuestTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { brandName_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ brandName_CONTAINS: root })) },
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "patientId",
			getWhereExpression: ({ isInverted, values }): QuestTableRowWhere =>
				isInverted
					? { records_NONE: { insuranceId_IN: values.map(({ root }) => root) } }
					: { records_SOME: { insuranceId_IN: values.map(({ root }) => root) } },
			options: toOptions(data.patientIds),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "questStatus",
			localizationKey: "quest-status",
			valueSequence: questStatuses,
			options: questStatuses.map((status) => ({ value: status, label: t(status.toLowerCase()), isSelected: false })),
			getWhereExpression: ({ isInverted, values }): QuestTableRowWhere => {
				if (values[0].root === QuestFilterStatus.UNRESOLVED || (isInverted && values[0].root === QuestFilterStatus.UNRESOLVED)) {
					return {
						completionStatus_IN: [QuestCompletionStatus.Active],
					};
				}
				return {
					completionStatus_NOT_IN: [QuestCompletionStatus.Active],
				} as any;
			},
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: option.label,
					color: statusColors[option.value as keyof typeof statusColors],
					backgroundColor: statusBackgroundColors[option.value as keyof typeof statusBackgroundColors],
					icon: statusIcons[option.value as keyof typeof statusIcons],
				}),
			isMulti: false,
			isPermanent: true,
			nonSearchable: true,
		},
		{
			selectionType: "list",
			field: "questOwnerName",
			localizationKey: "quest-owner",
			getWhereExpression: ({ isInverted, values }): QuestTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { questOwnerName_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ questOwnerName_CONTAINS: root })) },
			options: toOptions(data.questOwners),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "numeric-range",
			field: "value",
			minLabel: "min-amount",
			maxLabel: "max-amount",
			localizationKey: "quest-amount",
			getWhereExpression: (values: NumericFilterGetWhereExpressionArgs): QuestTableRowWhere =>
				({
					value_LTE: values.endValue,
					value_GTE: values.startValue,
				} as any),
			currencyCode: organization.currencyCodes[0]?.Code,
			rangeCounters: data.amountRanges,
			...createNumericInitialValues(data.amountRanges),
		},
		{
			selectionType: "date-range",
			field: "administrationDate",
			rangeType: "custom",
			getWhereExpression: (values: DateFilterGetWhereExpressionArgs): QuestTableRowWhere => ({
				records_SOME: {
					administrationDate_LTE: values.endValue || undefined,
					administrationDate_GTE: values.startValue || undefined,
				},
			}),
			min: data.minAdministrationDate,
			max: data.maxAdministrationDate,
		},
		{
			selectionType: "numeric-range",
			field: "quantity",
			minLabel: "min-quantity",
			maxLabel: "max-quantity",
			isWithoutBars: true,
			getWhereExpression: (values: NumericFilterGetWhereExpressionArgs): QuestTableRowWhere => ({
				records_SOME: { quantity_LTE: values.endValue, quantity_GTE: values.startValue },
			}),
			rangeCounters: data.quantityRanges,
			...createNumericInitialValues(data.quantityRanges),
		},
		{
			selectionType: "list",
			field: "recordStatus",
			localizationKey: "records-status",
			valueSequence: recordStatuses,
			getWhereExpression: ({ isInverted, values }): QuestTableRowWhere =>
				isInverted
					? { records_NONE: { status_IN: values.map(({ root }) => root as FlexFormStatus) } }
					: { records_SOME: { status_IN: values.map(({ root }) => root as FlexFormStatus) } },
			isMulti: false,
			isPermanent: false,
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: option.label,
					color: statusColors[option.value as keyof typeof statusColors],
					backgroundColor: statusBackgroundColors[option.value as keyof typeof statusBackgroundColors],
					icon: statusIcons[option.value as keyof typeof statusIcons],
				}),
			options: recordStatuses.map((status) => ({ value: status, label: t(status), isSelected: false })),
			nonSearchable: true,
		},
		{
			selectionType: "list",
			field: "recordId",
			getWhereExpression: ({ isInverted, values }): QuestTableRowWhere =>
				isInverted
					? { records_NONE: { id_IN: values.map(({ root }) => root) } }
					: { records_SOME: { id_IN: values.map(({ root }) => root) } },
			options: toOptions(data.recordIds),
			isMulti: true,
			isPermanent: false,
		},
	]);
};

export const mapRefundsTableFilterData = (queryData: GetRefundsFiltersQuery, t: TrFun, organization: UserOrg): FilterListStateType => {
	const data = queryData.GetRefundsTableSourceFilterData;
	const { hasOnlyCaselessAgreements, hasOnlyPatientAgreements } = queryData.GetOrganizationCaselessAgreementStatus;
	const getNestedStatusFilter = (status: RefundStatus): ListFilterState | undefined => {
		if (status === RefundStatus.Pending && !!data.reviewersOrganizations.length) {
			return {
				selectionType: "list",
				field: "counterpartyInReview",
				searchLabel: "search-reviewers",
				isMulti: true,
				label: "in-review-case-by",
				hasNestedOrNested: true,
				options: toOptions(data.reviewersOrganizations),
			};
		}

		return undefined;
	};
	const getStatusNestedFilterExpression = (
		status: RefundStatus,
		nestedValues: Array<string> | undefined
	): RefundTableRowWhere | undefined => {
		if (status === RefundStatus.Pending) {
			return nestedValues?.length ? { OR: nestedValues.map((nested) => ({ currentReviewerOrganization_CONTAINS: nested })) } : {};
		}

		return undefined;
	};

	const tagsFilter: FilterListStateType<GetRefundsQuery["refundTableRows"][number]> =
		organization.agreementTagNames.length > 1
			? [
					{
						selectionType: "list",
						field: "agreementTagTypes",
						localizationKey: "tags",
						getWhereExpression: ({ values }): RefundTableRowWhere => ({
							AND: values.map(({ root }) => ({ agreementTagTypes_INCLUDES: root })),
						}),
						renderOption: (option, isActiveContent) =>
							Badge({
								isActiveContent,
								isActive: option.isSelected,
								name: t(option.label),
							}),
						options: toOptions(data.tags),
						isPermanent: true,
						isMulti: true,
						nonSearchable: true,
						cannotBeInverted: true,
					} as ListFilterState<GetRefundsQuery["refundTableRows"][number]>,
			  ]
			: [];

	const refundStatuses: Array<RefundStatus> = [
		RefundStatus.New,
		RefundStatus.Pending,
		RefundStatus.Declined,
		RefundStatus.Invoiced,
		RefundStatus.Settled,
	];

	const lastAdjustedAtCustomRanges = [...defaultDateRanges, "empty"] as DateRangeType[];
	// any used bc types were not generated

	const TRUE_AS_STRING = "true";
	const FALSE_AS_STRING = "false";

	return setInitialListStates<
		GetRefundsQuery["refundTableRows"][number] & {
			purchaseOrPatientId: string;
			caseOrAgreementId: string;
			recordTypeLocalizationKey: string;
		}
	>([
		...tagsFilter,
		{
			selectionType: "list",
			field: "purchaseOrPatientId",
			localizationKey: getPurchaseOrPatientFieldName(hasOnlyPatientAgreements, hasOnlyCaselessAgreements, t),
			options: toOptions(data.purchaseOrPatientIds),
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "recordTypeLocalizationKey",
			getWhereExpression: ({ isInverted, values }): RefundTableRowWhere =>
				isInverted
					? { NOT: { recordTypeLocalizationKey_IN: values.map(({ root }) => root) } }
					: { recordTypeLocalizationKey_IN: values.map(({ root }) => root) },
			options: data.recordTypes.map((type) => ({ label: t(type), value: type })) as Array<ListFilterOption>,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "counterParties",
			localizationKey: "counterparties",
			getWhereExpression: ({ isInverted, values }): RefundTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { counterParties_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ counterParties_CONTAINS: root })) },
			options: toOptions(data.counterparties),
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "product",
			localizationKey: "pack",
			options: toOptions(data.products),
			getWhereExpression: ({ isInverted, values }): RefundTableRowWhere =>
				isInverted ? { NOT: { product_IN: values.map(({ root }) => root) } } : { product_IN: values.map(({ root }) => root) },
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "status",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): RefundTableRowWhere => ({
				OR: values.map(({ root, nested }) => ({
					status: root as RefundStatus,
					...getStatusNestedFilterExpression(root as RefundStatus, nested as Array<string>),
				})),
			}),
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: option.label,
					color: statusColors[option.value as keyof typeof statusColors],
					backgroundColor: statusBackgroundColors[option.value as keyof typeof statusBackgroundColors],
					icon: statusIcons[option.value as keyof typeof statusIcons],
				}),
			valueSequence: refundStatuses,
			isMulti: true,
			options: refundStatuses.map((status) => ({
				value: status,
				label: t(transformTransactionStatusNames(status)),
				isSelected: false,
				nested: getNestedStatusFilter(status as RefundStatus),
			})),
			isPermanent: true,
			nonSearchable: true,
		} as ListFilterState<GetRefundsQuery["refundTableRows"][number]>,
		{
			selectionType: "numeric-range",
			field: "finalRefundablePercentage",
			localizationKey: "refund-rate",
			currencyCode: "%",
			rangeCounters: data.rateRanges,
			...createNumericInitialValues(data.rateRanges),
		},
		{
			selectionType: "numeric-range",
			field: "finalAmount",
			localizationKey: "refund-amount",
			currencyCode: organization.currencyCodes?.[0]?.Code,
			rangeCounters: data.amountRanges,
			...createNumericInitialValues(data.amountRanges),
		},
		{
			selectionType: "numeric-range",
			isWithoutBars: true,
			field: "quantity",
			getWhereExpression: (values: NumericFilterGetWhereExpressionArgs): RefundTableRowWhere => ({
				quantity_LTE: values.endValue,
				quantity_GTE: values.startValue,
			}),
			rangeCounters: data.quantityRanges,
			...createNumericInitialValues(data.quantityRanges),
		},
		{
			selectionType: "numeric-range",
			field: "basePrice",
			getWhereExpression: (values: NumericFilterGetWhereExpressionArgs): RefundTableRowWhere => ({
				basePrice_LTE: values.endValue,
				basePrice_GTE: values.startValue,
			}),
			rangeCounters: data.basePricesRanges,
			...createNumericInitialValues(data.basePricesRanges),
		},
		{
			selectionType: "date-range",
			field: "invoiceDate",
			getWhereExpression: (values: DateFilterGetWhereExpressionArgs) => ({
				invoiceDate_LTE: values.endValue || undefined,
				invoiceDate_GTE: values.startValue || undefined,
			}),
			rangeType: "custom",
			min: data.minInvoiceDate,
			max: data.maxInvoiceDate,
		},
		{
			selectionType: "date-range",
			field: "prescriptionDate",
			localizationKey: getAdministrationOrPurchaseDateFieldName(hasOnlyPatientAgreements, hasOnlyCaselessAgreements, t),
			getWhereExpression: (values: DateFilterGetWhereExpressionArgs): RefundTableRowWhere => ({
				prescriptionDate_LTE: values.endValue || undefined,
				prescriptionDate_GTE: values.startValue || undefined,
			}),
			rangeType: "custom",
			min: data.minAdministrationDate,
			max: data.maxAdministrationDate,
		},
		{
			selectionType: "date-range",
			field: "lastAdjustedAt",
			customRanges: lastAdjustedAtCustomRanges,
			localizationKey: t("transaction-last-adjusted-date"),
			getWhereExpression: (values: DateFilterGetWhereExpressionArgs): RefundTableRowWhere => {
				if (values.startValue === filterNullableValue) {
					return { lastAdjustedAt_IS_NULL: true };
				}

				return {
					lastAdjustedAt_LTE: values.endValue || undefined,
					lastAdjustedAt_GTE: values.startValue || undefined,
				};
			},
			rangeType: "custom",
			min: data.minAdjustedAt,
			max: data.maxAdjustedAt,
		},
		{
			selectionType: "list",
			field: "legalEntityName",
			getWhereExpression: ({ isInverted, values }): RefundTableRowWhere =>
				isInverted
					? { NOT: { legalEntityName_IN: values.map(({ root }) => root) } }
					: { legalEntityName_IN: values.map(({ root }) => root) },
			localizationKey: "legal-entity",
			options: toOptions(data.legalEntities),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "caseOrAgreementId",
			localizationKey: getCaseOrAgreementIdFieldName(hasOnlyPatientAgreements, hasOnlyCaselessAgreements, t),
			options: toOptions(data.caseOrAgreementIds),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "id",
			localizationKey: "transaction-id",
			options: toOptions(data.ids),
			isMulti: true,
		},
		{
			selectionType: "list",
			localizationKey: "is-delta",
			field: "isDelta",
			cannotBeInverted: true,
			valueSequence: [TRUE_AS_STRING, FALSE_AS_STRING],
			isMulti: true,
			options: [TRUE_AS_STRING, FALSE_AS_STRING].map((value) => ({
				value,
				label: value === TRUE_AS_STRING ? t("delta-YES") : t("delta-NO"),
				isSelected: false,
			})),
			getWhereExpression: (values: ListFilterGetWhereExpressionArgs): RefundTableRowWhere => {
				const BOTH_OPTIONS_LENGTH = 2;
				if (values.values.length === BOTH_OPTIONS_LENGTH) {
					return {};
				}

				return {
					isDelta: values.values[0].root === TRUE_AS_STRING,
				};
			},
			nonSearchable: true,
			isBooleanList: true,
		},
		{
			selectionType: "list",
			field: "documentId",
			localizationKey: "documentId",
			options: toOptions(data.documentIds),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "predecessorIds",
			localizationKey: "predecessorIds",
			options: toOptions(data.predecessorIds),
			getWhereExpression: ({ isInverted, values }): RefundTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { predecessorIds_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ predecessorIds_CONTAINS: root })) },
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "successorIds",
			localizationKey: "successorIds",
			options: toOptions(data.successorIds),
			getWhereExpression: ({ isInverted, values }): RefundTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { successorIds_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ successorIds_CONTAINS: root })) },
			isMulti: true,
		},
	]);
};

export const mapDocumentPageFilterData = (
	data: Array<DocumentRefundGroup>,
	baseAgreements: Array<BaseAgreement>,
	isDraft: boolean,
	isCaseless: boolean,
	dateAttributeNames: Array<string>,
	t: TrFun
): FilterListStateType => {
	const recordDates = data.flatMap((g) => g.refunds.map((r) => r.documentFinancialDataPointConnection.edges[0].node.prescriptionDate));
	const minAdministrationDate = minBy(recordDates, identity) ?? null;
	const maxAdministrationDate = maxBy(recordDates, identity) ?? null;

	return setInitialListStates([
		{
			selectionType: "list",
			field: "status",
			localizationKey: "decision",
			options: toOptions(acceptanceStatuses).map((option) => ({
				...option,
				count: data
					.flatMap((group) => group.refunds)
					.reduce((total, currentRefund) => (getRefundStatus(currentRefund) === option.value ? total + 1 : total), 0),
			})),
			isPermanent: true,
			isMulti: true,
			renderOption: (option) => {
				return StatusOption({ status: option.label, count: option.count ?? 0 });
			},
		},
		...((isCaseless
			? []
			: [
					{
						selectionType: "list",
						field: "caseId",
						localizationKey: "caseId",
						options: toOptions(
							distinct(
								data
									.map((group) => getRefundGroupInfo(group, baseAgreements).caseEntity?.id)
									.filter((id): id is string => Boolean(id))
							)
						),
						isPermanent: true,
						isMulti: true,
					},
					{
						selectionType: "list",
						field: "patientId",
						localizationKey: "patientId",
						options: toOptions(
							distinct(
								data
									.map((group) => getRefundGroupInfo(group, baseAgreements).caseEntity?.patientInsuranceId)
									.filter((id): id is string => Boolean(id))
							)
						),
						isPermanent: true,
						isMulti: true,
					},
					{
						selectionType: "list",
						field: "scopeId",
						localizationKey: "scopeId",
						options: toOptions(
							distinct(
								data
									.map((group) => {
										const { countryScopeId } = getGroupScopes(group, baseAgreements);

										return getCombinedScopeIds({ countryScopeId });
									})
									.filter((scope): scope is string => Boolean(scope))
							)
						),
						isPermanent: true,
						isMulti: true,
					},
			  ]) as Array<ListFilterState>),
		{
			selectionType: "list",
			field: "agreementId",
			localizationKey: "agreement",
			options: distinctBy(
				data
					.map((group) => {
						const { agreement } = getRefundGroupInfo(group, baseAgreements);

						return {
							label: agreement?.name,
							value: agreement?.id,
							isSelected: false,
						};
					})
					.filter((option): option is ListFilterOption => Boolean(option.label)),
				(option) => option.label
			).sort(),
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "pack",
			localizationKey: "pack",
			options: distinct(
				distinctBy(
					data.flatMap((group) => {
						const { packs } = getRefundGroupInfo(group, baseAgreements);

						return packs;
					}),
					(p) => p.Code
				).map((p) => formatTreatment(p))
			)
				.map((packName) => ({ label: packName, value: packName, isSelected: false }))
				.sort(),
			isPermanent: true,
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "purchaseId",
			localizationKey: "purchaseId",
			options: toOptions(
				distinct(
					data.flatMap((r) =>
						r.refunds
							.map((r) => r.documentFinancialDataPointConnection.edges[0].node.evaluates.purchaseId as string)
							.filter(Boolean)
					)
				)
			),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "refundId",
			localizationKey: "transactionId",
			options: toOptions(distinct(data.flatMap((r) => r.refunds.map((r) => r.id as string).filter(Boolean)))),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "date-range",
			field: "date",
			localizationKey: dateAttributeNames.map((name) => t(name)).join("/"),
			rangeType: "custom",
			min: minAdministrationDate,
			max: maxAdministrationDate,
			isPermanent: true,
		},
		{
			selectionType: "chip",
			isPermanent: true,
			field: "messages",
			localizationKey: "has-messages-filter",
			icons: { iconOn: filterIcons["MessagesIcon"], iconOff: filterIcons["MessagesBorderIcon"] },
		} as ChipFilterState,
		{
			selectionType: "chip",
			isPermanent: true,
			field: "adjusted",
			localizationKey: "adjusted",
			icons: { iconOn: filterIcons["AdjustedBorderIcon"], iconOff: filterIcons["AdjustedIcon"] },
		} as ChipFilterState,
		{
			selectionType: "chip",
			isPermanent: true,
			field: "isDelta",
			localizationKey: "is-delta",
			icons: { iconOn: filterIcons["DeltaBorderIcon"], iconOff: filterIcons["DeltaIcon"] },
		} as ChipFilterState,
		{
			selectionType: "chip",
			isPermanent: true,
			field: "wasRemoved",
			localizationKey: "wasRemoved",
		} as ChipFilterState,
	]);
};

export const mapDocumentsTableFilterData = (
	data: GetDocumentsFiltersQuery["GetDocumentsTableSourceFilterData"],
	t: TrFun,
	organization: UserOrg
): FilterListStateType => {
	const tagsFilter: FilterListStateType<GetDocumentsQuery["documentTableRows"][number]> =
		organization.agreementTagNames.length > 0
			? [
					{
						selectionType: "list",
						field: "formattedTags",
						localizationKey: "tags",
						getWhereExpression: ({ isInverted, values }): DocumentTableRowWhere =>
							isInverted
								? { AND: values.map(({ root }) => ({ NOT: { agreementTagTypes_INCLUDES: root } })) }
								: { OR: values.map(({ root }) => ({ agreementTagTypes_INCLUDES: root })) },
						options: toOptions(data.tags),
						renderOption: (option, isActiveContent) =>
							Badge({
								isActiveContent,
								isActive: option.isSelected,
								name: t(option.label),
							}),
						isMulti: true,
						nonSearchable: true,
					} as ListFilterState<GetDocumentsQuery["documentTableRows"][number]>,
			  ]
			: [];

	const documentStatuses: Array<DocumentStatus> = [
		DocumentStatus.WorkingDraft,
		DocumentStatus.InReview,
		DocumentStatus.InApproval,
		DocumentStatus.Issued,
		DocumentStatus.Settled,
	];

	const getNestedStatusFilter = (status: DocumentStatus): ListFilterState | undefined => {
		if (status === DocumentStatus.InReview || status === DocumentStatus.InApproval) {
			return {
				selectionType: "list",
				field: "counterpartyInReview",
				searchLabel: "search-reviewers",
				isMulti: true,
				label: "in-review-case-by",
				hasNestedOrNested: true,
				options: toOptions(status === DocumentStatus.InReview ? data.reviewersOrganizations : data.approversOrganizations),
			};
		}

		return undefined;
	};

	const getStatusNestedFilterExpression = (
		status: DocumentStatus,
		nestedValues: Array<string> | undefined
	): DocumentTableRowWhere | undefined => {
		if (status === DocumentStatus.InReview || status === DocumentStatus.InApproval) {
			return nestedValues?.length ? { OR: nestedValues.map((nested) => ({ currentReviewerOrganization_CONTAINS: nested })) } : {};
		}

		return undefined;
	};

	return setInitialListStates<GetDocumentsQuery["documentTableRows"][number]>([
		{
			selectionType: "list",
			field: "counterParties",
			localizationKey: "counterparties",
			getWhereExpression: ({ isInverted, values }): DocumentTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { counterParties_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ counterParties_CONTAINS: root })) },
			options: toOptions(data.counterparties),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "product",
			getWhereExpression: ({ isInverted, values }): DocumentTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { product_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ product_CONTAINS: root })) },
			options: toOptions(data.brandNames),
			isMulti: true,
			isPermanent: true,
		},
		{
			selectionType: "list",
			field: "beneficiary",
			localizationKey: "document-beneficiary",
			options: [
				{ label: t("document-beneficiary-lyfegen"), value: DocumentBeneficiaryEnum.Lyfegen, isSelected: false },
				{ label: t("document-beneficiary-payer"), value: DocumentBeneficiaryEnum.Payer, isSelected: false },
			].filter((o) => data.beneficiaries.includes(o.value)),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "paymentReferenceLyfegen",
			localizationKey: "document-payment-reference-lyfegen",
			options: toOptions(data.paymentReferenceLyfegens),
			isMulti: true,
		},
		{
			selectionType: "date-range",
			field: "paymentReceiptPayer",
			localizationKey: "document-payment-receipt-payer",
			rangeType: "custom",
			min: data.minPaymentReceiptPayer,
			max: data.maxPaymentReceiptPayer,
		},
		{
			selectionType: "list",
			field: "status",
			cannotBeInverted: true,
			getWhereExpression: ({ isInverted, values }): DocumentTableRowWhere => ({
				OR: values.map(({ root, nested }) => ({
					status: root as DocumentStatus,
					...getStatusNestedFilterExpression(root as DocumentStatus, nested as Array<string>),
				})),
			}),
			valueSequence: documentStatuses,
			renderOption: (option, isActiveContent) =>
				Badge({
					isActive: option.isSelected,
					isActiveContent,
					name: option.label,
					color: statusColors[option.value as keyof typeof statusColors],
					backgroundColor: statusBackgroundColors[option.value as keyof typeof statusBackgroundColors],
					icon: statusIcons[option.value as keyof typeof statusIcons],
				}),

			options: documentStatuses.map((status) => ({
				value: status,
				label: t(status),
				isSelected: false,
				nested: getNestedStatusFilter(status as DocumentStatus),
			})),
			isPermanent: true,
			nonSearchable: true,
			isMulti: true,
		} as ListFilterState<GetDocumentsQuery["documentTableRows"][number]>,
		{
			selectionType: "numeric-range",
			field: "finalInvoiceAmount",
			localizationKey: "invoice-amount",
			currencyCode: organization.currencyCodes?.[0]?.Code,
			rangeCounters: data.amountRanges,
			...createNumericInitialValues(data.amountRanges),
		},
		{
			selectionType: "date-range",
			field: "coveragePeriodStart",
			localizationKey: "coverage-period",
			isOnlyCustomAvailable: true,
			rangeType: "custom",
			getWhereExpression: (values: DateFilterGetWhereExpressionArgs): DocumentTableRowWhere => ({
				coveragePeriodStart_LTE: values.endValue || undefined,
				coveragePeriodEnd_GTE: values.startValue || undefined,
			}),
			min: data.minCoverageDate,
			max: data.maxCoverageDate,
		},
		...tagsFilter,
		{
			selectionType: "date-range",
			field: "createdOn",
			localizationKey: "created-on",
			rangeType: "custom",
			min: data.minCreatedOn,
			max: data.maxCreatedOn,
		},
		{
			selectionType: "date-range",
			field: "lastRemindedOn",
			localizationKey: "last-reminded-on",
			rangeType: "custom",
			min: data.minLastRemindedOn,
			max: data.maxLastRemindedOn,
		},
		{
			selectionType: "date-range",
			field: "reviewRemindedOn",
			localizationKey: "review-reminded-on",
			rangeType: "custom",
			min: data.minReviewRemindedOn,
			max: data.maxReviewRemindedOn,
		},
		{
			selectionType: "date-range",
			field: "sentToReviewOn",
			localizationKey: "sent-to-review-on",
			rangeType: "custom",
			min: data.minSentToReviewOn,
			max: data.maxSentToReviewOn,
		},
		{
			selectionType: "date-range",
			field: "issuedOn",
			localizationKey: "issued-on",
			rangeType: "custom",
			min: data.minIssuedOn,
			max: data.maxIssuedOn,
		},
		{
			selectionType: "list",
			field: "type",
			options: data.types.map((documentType) => ({ value: documentType, label: t(documentType), isSelected: false })),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "id",
			localizationKey: "documentId",
			options: toOptions(data.ids),
			isMulti: true,
		},
		{
			selectionType: "list",
			field: "formattedApprovers",
			localizationKey: "approvers",
			options: toOptions(data.approvers),
			isMulti: true,
			getWhereExpression: ({ isInverted, values }): DocumentTableRowWhere =>
				isInverted
					? { AND: values.map(({ root }) => ({ NOT: { formattedApprovers_CONTAINS: root } })) }
					: { OR: values.map(({ root }) => ({ formattedApprovers_CONTAINS: root })) },
		},
		{
			selectionType: "list",
			field: "smartMipReimbursementId",
			localizationKey: "smartMipReimbursementId",
			options: toOptions(data.smartMipReimbursementIds),
			isMulti: true,
		},
	]);
};

export const useAgreementsPersistedFilters = (isDashboard?: boolean) => {
	const { loading, data } = useQuery<GetAgreementsFilters>(GET_AGREEMENTS_FILTERS, { fetchPolicy: "network-only" });
	const organization = useRecoilValue(userOrganization);
	const { t } = useTranslation();
	const deserializedFilters = data?.filters && JSON.parse(data?.filters);
	const filters = useMemo(
		() =>
			data &&
			setLoadedFilterValues(
				mapAgreementsTableFilterData(data?.GetAgreementsTableSourceFilterData, t, organization, !!isDashboard),
				deserializedFilters
			),
		[data]
	);

	return { loading, filters, deserializedFilters };
};

export const usePublicAgreementsPersistedFilters = () => {
	const { loading, data } = useModelsQuery<GetPublicAgreementsFiltersQuery>(GET_PUBLIC_AGREEMENTS_FILTERS, {
		fetchPolicy: "network-only",
	});
	const { t } = useTranslation();

	const deserializedFilters = useMemo(() => data?.filters && JSON.parse(data?.filters), [data]);

	const filters = useMemo(
		() =>
			data &&
			setLoadedFilterValues(mapPublicAgreementsTableFilterData(data?.GetPublicAgreementsSourceFilterData, t), deserializedFilters),
		[data]
	);

	return { loading, filters, deserializedFilters };
};

export const useModelsPersistedFilters = () => {
	const { loading, data } = useModelsQuery<GetModelsFiltersQuery>(GET_MODEL_FILTERS, { fetchPolicy: "network-only" });
	const { t } = useTranslation();
	const deserializedFilters = data?.filters && JSON.parse(data?.filters);
	const filters = useMemo(
		() => data && setLoadedFilterValues(mapModelsTableFilterData(data.GetModelsSourceFilterData, t), deserializedFilters),
		[data]
	);

	return { loading, filters, deserializedFilters };
};

export const useCasesPersistedFilters = () => {
	const { loading, data } = useQuery<GetCasesFiltersQuery>(GET_CASES_FILTERS, { fetchPolicy: "network-only" });
	const organization = useRecoilValue(userOrganization);
	const { t } = useTranslation();
	const filters = useMemo(
		() => data && setLoadedFilterValues(mapCasesTableFilterData(data?.GetCasesTableSourceFilterData, t, organization), data.filters),
		[data]
	);

	return { loading, filters };
};

export const useFormsPersistedFilters = () => {
	const { loading, data } = useQuery<GetFormsFiltersQuery>(GET_FORM_FILTERS, { fetchPolicy: "network-only" });
	const { t } = useTranslation();
	const filters = useMemo(
		() => data && setLoadedFilterValues(mapFormsTableFilterData(data?.GetFormsTableSourceFilterData, t), data.filters),
		[data]
	);

	return { loading, filters };
};

export const useSimulationsPersistedFilters = () => {
	const { data: globalDomainName, loading: domainLoading } = useModelsQuery<GetUserDomainNameQuery>(GET_USER_GLOBAL_DOMAIN_NAME);
	const {
		loading,
		data,
		refetch: refetchFilters,
	} = useModelsQuery<GetSimulationsFiltersQuery>(GET_SIMULATION_FILTERS, {
		fetchPolicy: "network-only",
		skip: domainLoading,
	});

	const sortDomainOptions = (dataArray?: any[], globalDomainLabel?: string | null) => {
		if (!Array.isArray(dataArray)) return [];
		return dataArray.map((item) => {
			if (item.field === "domainName" && Array.isArray(item.options)) {
				const globalOption = item.options.find((opt: { label: string | null | undefined }) => opt.label === globalDomainLabel);
				const filteredOptions = item.options.filter((opt: { label: string | null | undefined }) => opt.label !== globalDomainLabel);
				const sortedOptions = sortOrganizationDomainsLabel(filteredOptions);
				return {
					...item,
					options: globalOption ? [globalOption, ...sortedOptions] : sortedOptions,
				};
			}
			return item;
		});
	};

	const { t } = useTranslation();
	const filters = useMemo(
		() => data && setLoadedFilterValues(mapSimulationTableFilterData(data?.GetSimulationsTableSourceFilterData, t), data.filters),
		[data]
	);

	const sortedFilters = sortDomainOptions(filters, globalDomainName?.GetUserDomainName[0]);
	return { loading: loading || domainLoading, filters: sortedFilters, refetchFilters };
};

export const useRecordsPersistedFilters = () => {
	const { loading, data } = useQuery<GetRecordsFiltersQuery>(GET_RECORD_FILTERS, { fetchPolicy: "network-only" });
	const { t } = useTranslation();
	const filters = useMemo(() => data && setLoadedFilterValues(mapRecordsTableFilterData(data, t), data.filters), [data]);

	return { loading, filters };
};

export const useQuestsPersistedFilters = () => {
	const { loading, data } = useQuery<GetQuestsFiltersQuery>(GET_QUEST_FILTERS, { fetchPolicy: "network-only" });
	const organization = useRecoilValue(userOrganization);
	const { t } = useTranslation();
	const filters = useMemo(
		() => data && setLoadedFilterValues(mapQuestsTableFilterData(data?.GetQuestsTableSourceFilterData, t, organization), data.filters),
		[data]
	);

	return { loading, filters };
};

export const useRefundsPersistedFilters = () => {
	const { loading, data } = useQuery<GetRefundsFiltersQuery>(GET_REFUNDS_FILTERS, { fetchPolicy: "network-only" });
	const setOrganizationAgreementsStatus = useSetRecoilState(organizationAgreementState);
	const organization = useRecoilValue(userOrganization);
	const { t } = useTranslation();
	const filters = useMemo(() => data && setLoadedFilterValues(mapRefundsTableFilterData(data, t, organization), data.filters), [data]);
	useEffect(() => {
		setOrganizationAgreementsStatus({
			hasOnlyCaselessAgreements: data?.GetOrganizationCaselessAgreementStatus.hasOnlyCaselessAgreements || false,
			hasOnlyPatientAgreements: data?.GetOrganizationCaselessAgreementStatus.hasOnlyPatientAgreements || false,
		});
	}, [data]);

	return { loading, filters };
};

export const useDocumentsPersistedFilters = () => {
	const { loading, data } = useQuery<GetDocumentsFiltersQuery>(GET_DOCUMENTS_FILTERS, { fetchPolicy: "network-only" });
	const organization = useRecoilValue(userOrganization);
	const { t } = useTranslation();
	const filters = useMemo(
		() =>
			data &&
			setLoadedFilterValues(mapDocumentsTableFilterData(data?.GetDocumentsTableSourceFilterData, t, organization), data.filters),
		[data]
	);

	return { loading, filters };
};

export const useDocumentPagePersistedFilters = (
	data: Array<DocumentRefundGroup>,
	baseAgreements: Array<BaseAgreement>,
	recoilKeyword: string,
	isCaseless: boolean,
	isDraft: boolean,
	dateAttributeNames: Array<string>,
	messages: Array<DocumentMessageModel>
) => {
	const [search, handleSearch] = useState("");
	const { t } = useTranslation();

	const storedFilters = useRecoilValue(documentPageFilterFamily(recoilKeyword));
	const initialFilters = useMemo(
		() =>
			data.length
				? setLocalFilterValues(
						mapDocumentPageFilterData(data, baseAgreements, isDraft, isCaseless, dateAttributeNames, t),
						storedFilters
				  )
				: storedFilters,
		[data, storedFilters, baseAgreements, isDraft, isCaseless]
	);
	const initialFiltersHash = hashSum(initialFilters);
	const [currentFilters, dispatchCurrentFilters] = useReducer(filterReducer, initialFilters);

	useEffect(() => {
		if (initialFilters) {
			dispatchCurrentFilters({ type: "restore-state", state: initialFilters });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialFiltersHash]);

	const filteredData = useMemo(
		() => handleDocumentPageFilters(data, currentFilters, baseAgreements, messages, search, t),
		[data, currentFilters, search, messages]
	);

	return { currentFilters, dispatchCurrentFilters, handleSearch, filteredData };
};
